import {
    Box,
    DatePicker,
    NullableDate,
    OSKIcon,
    OSKThemeType,
    SidePanel,
    Text,
    Divider,
    Button,
    Tooltip,
    TextInput,
    Typography,
    Spinner,
} from 'oskcomponents';
import { OrdersAPI, OSKGeoJson } from 'oskcore';
import React, { useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useTheme } from 'styled-components';
import { useDisableFeatureOnMouseOver, useMap } from '~/hooks';
import { setOpportunitySearchEndDate, setOpportunitySearchStartDate } from '~/redux/modules/tasking/app';
import { RootState } from '~/redux/store';
import GeocodingSearch from '../GeocodingSearch';
import { useNavigate } from 'react-router-dom';
import { LatLng } from 'leaflet';
import * as L from 'leaflet';
import { setRoi } from '~/redux/modules/data/search';
import { date_format_long } from '~/utils';
import { TaskingOrderRequest } from 'oskcore/src/api/orders';

// TODO: Replace this hard-coded product with a more dynamic selection
const TASKING_PRODUCT = 'L1B_TASKED';

type OpportunitySidePanelProps = {
    /** From redux, the selected AOI if any. */
    taskingAoi?: OSKGeoJson;
};

export const OpportunitySidePanel = ({ taskingAoi }: OpportunitySidePanelProps) => {
    const theme = useTheme() as OSKThemeType;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const map = useMap();

    const [orderName, setOrderName] = useState<string>();
    const [orderDescription, setOrderDescription] = useState<string>();
    const [searchStartDate, setSearchStartDate] = useState<NullableDate>(new Date());
    const [searchEndDate, setSearchEndDate] = useState<NullableDate>();
    const [latitude, setLatitude] = useState<number | undefined>();
    const [longitude, setLongitude] = useState<number | undefined>();
    const [validationError, setValidationError] = useState<string>('');
    const [submitError, setSubmitError] = useState<string>('');
    const [orderState, setOrderState] = useState<'order' | 'submitting' | 'complete'>('order');

    const panelRef = useRef<HTMLDivElement>(null);

    useDisableFeatureOnMouseOver(panelRef, 'Zoom', true);
    useDisableFeatureOnMouseOver(panelRef, 'Drag', true);
    useDisableFeatureOnMouseOver(panelRef, 'Click', true);

    const resetPanel = () => {
        setSearchStartDate(null);
        setSearchEndDate(null);
        setLatitude(undefined);
        setLongitude(undefined);
        setOrderName(undefined);
        dispatch(setRoi());
    };

    const updateRoi = (lat: number | undefined, lng: number | undefined) => {
        if (latitude && longitude) {
            const coord = { lat, lng } as LatLng;
            dispatch(setRoi(OSKGeoJson.fromCoordinate(coord)));
        }
    };

    useEffect(() => {
        if (taskingAoi && taskingAoi.features.length > 0 && taskingAoi.features[0].type === 'Point') {
            const coords = taskingAoi.features[0].coordinates;
            setLatitude(coords[1]);
            setLongitude(coords[0]);
        } else {
            setLatitude(undefined);
            setLongitude(undefined);
        }
    }, [taskingAoi]);

    useEffect(() => {
        // First time we load this, clear the roi if it's not a point
        if (taskingAoi?.features && taskingAoi?.features.length > 0) {
            const aoiType = (taskingAoi.features[0] as any).type;
            if (aoiType !== 'Point') {
                dispatch(setRoi());
            }
        }
    }, []);

    useEffect(() => {
        const errors: string[] = [];
        if (!orderName) {
            errors.push('An order name is required.');
        } else if (orderName.length < 8) {
            errors.push('Order name must be at least 8 characters long.');
        }

        if (!searchStartDate && !searchEndDate) {
            errors.push('A start and end date are required.');
        } else if (!searchStartDate) {
            errors.push('A start date is required.');
        } else if (!searchEndDate) {
            errors.push('An end date is required.');
        } else if (searchStartDate >= searchEndDate) {
            errors.push('The end date must be after the start date.');
        }

        if (taskingAoi?.isEmpty()) {
            errors.push('A target point is required.');
        }

        setValidationError(errors.join(' '));
    }, [orderName, searchEndDate, searchStartDate, taskingAoi]);

    if (orderState === 'complete') {
        return (
            <Box style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
                <SidePanel hideOverflow={false}>
                    <Box
                        ref={panelRef}
                        id="tasking-panel"
                        col
                        grow
                        center="horizontal"
                        style={{ padding: '60px 40px' }}
                    >
                        <OSKIcon
                            code="check-circle"
                            fill={theme.colors.green500}
                            scale={600}
                            style={{ marginBottom: '50px' }}
                        />
                        <Typography variant="heading2" style={{ textAlign: 'center' }}>
                            Tasking Request Received
                        </Typography>

                        <Box
                            style={{
                                border: `1px solid ${theme.colors.black200}`,
                                backgroundColor: 'white',
                                marginTop: '20px',
                                padding: '20px',
                                width: 'fit-content',
                                height: '160px',
                                justifyContent: 'space-between',
                            }}
                            col
                        >
                            {orderName && (
                                <Box>
                                    <Typography variant="body3" strong style={{ marginRight: '8px' }}>
                                        Order Name:
                                    </Typography>
                                    <Typography variant="body3">{orderName}</Typography>
                                </Box>
                            )}
                            <Box>
                                <Typography variant="body3" strong style={{ marginRight: '8px' }}>
                                    Latitude:
                                </Typography>
                                <Typography variant="body3">{latitude}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body3" strong style={{ marginRight: '8px' }}>
                                    Longitude:
                                </Typography>
                                <Typography variant="body3">{longitude}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body3" strong style={{ marginRight: '8px' }}>
                                    Start Date:
                                </Typography>
                                <Typography variant="body3">{date_format_long(searchStartDate)}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body3" strong style={{ marginRight: '8px' }}>
                                    End Date:
                                </Typography>
                                <Typography variant="body3">{date_format_long(searchEndDate)}</Typography>
                            </Box>
                        </Box>

                        <Typography
                            variant="body3"
                            style={{
                                textAlign: 'center',
                                color: theme.colors.black600,
                                margin: '35px 0px',
                                lineHeight: '180%',
                                width: '175px',
                            }}
                        >
                            Visit your <a href="/myorders">Orders Page</a> to view &amp; track the progress of your
                            Tasking Request.
                        </Typography>

                        <Button
                            variant="action"
                            onClick={() => {
                                resetPanel();
                                setOrderState('order');
                            }}
                        >
                            <Typography variant="body3">Create New Tasking Request</Typography>
                        </Button>
                    </Box>
                </SidePanel>
            </Box>
        );
    }

    return (
        <Box style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
            <SidePanel hideOverflow={false}>
                <Box ref={panelRef} id="tasking-panel" col grow>
                    <Typography variant="heading2">Tasking Order</Typography>
                    <GeocodingSearch
                        style={{
                            margin: '20px 0px',
                        }}
                        onSelected={(location, bounds) => {
                            const url = `/tasking/@${location.lat},${location.lng}`;
                            navigate(url, { replace: true });

                            if (bounds) {
                                // If we have a boundary, let's fit the whole thing
                                const latLngBounds = L.latLngBounds(bounds.southwest, bounds.northeast);
                                map.fitCoordinates([OSKGeoJson.fromLatLngBounds(latLngBounds)], 0.1);
                            } else {
                                // Otherwise, if we just have a point that's ok too.
                                const coord = { lat: location.lat, lng: location.lng } as LatLng;
                                map.fitCoordinates([OSKGeoJson.fromCoordinate(coord)], 0.1);
                            }
                            dispatch(setRoi(OSKGeoJson.fromCoordinate(location)));
                        }}
                    />
                    <Divider style={{ margin: '0px 0px 20px 0px' }} />

                    <Typography variant="heading4" style={{ marginBottom: '4px' }}>
                        Order Name
                    </Typography>
                    <Box
                        style={{
                            justifyContent: 'space-between',
                        }}
                    >
                        <TextInput
                            name="order-name"
                            variant="contrast"
                            style={{
                                width: '100%',
                                height: '45px',
                            }}
                            placeholder="Enter a name for this order"
                            value={orderName}
                            onChange={(e) => setOrderName(e.target.value)}
                        />
                    </Box>

                    <Typography variant="heading4" style={{ marginBottom: '4px', marginTop: '20px' }}>
                        Order Description
                    </Typography>
                    <Box
                        style={{
                            justifyContent: 'space-between',
                        }}
                    >
                        <TextInput
                            name="order-desc"
                            variant="contrast"
                            style={{
                                width: '100%',
                                height: '45px',
                            }}
                            placeholder="Enter a description for this order"
                            value={orderDescription}
                            onChange={(e) => setOrderDescription(e.target.value)}
                        />
                    </Box>

                    <Divider style={{ margin: '20px 0px 20px 0px' }} />

                    <Typography variant="heading4" style={{ marginBottom: '4px', marginTop: '20px' }}>
                        Latitude
                    </Typography>
                    <Box
                        style={{
                            justifyContent: 'space-between',
                            position: 'relative',
                        }}
                    >
                        <TextInput
                            name="order-latitude"
                            variant="contrast"
                            style={{
                                width: '100%',
                                height: '45px',
                                marginBottom: '20px',
                            }}
                            placeholder="e.g. 37.779'"
                            value={latitude}
                            onChange={(e) => {
                                const lat = parseFloat(e.target.value);

                                if (!isNaN(lat)) {
                                    setLatitude(lat);

                                    updateRoi(lat, longitude);
                                }
                            }}
                        />
                        <Button
                            variant="action"
                            style={{ position: 'absolute', right: '0px', top: '-55px', padding: '8px 14px' }}
                            onClick={() => {
                                if (latitude && longitude) {
                                    const url = `/tasking/@${latitude},${longitude}`;
                                    navigate(url, { replace: true });

                                    const coord = { lat: latitude, lng: longitude } as LatLng;
                                    map.fitCoordinates([OSKGeoJson.fromCoordinate(coord)], 0.1);
                                }
                            }}
                        >
                            <Box center="all">
                                <Typography variant="body3" strong style={{ marginRight: '4px' }}>
                                    Go To
                                </Typography>
                                <OSKIcon code="crosshair" fill="white" />
                            </Box>
                        </Button>
                    </Box>

                    <Typography variant="heading4" style={{ marginBottom: '4px' }}>
                        Longitude
                    </Typography>
                    <Box
                        style={{
                            justifyContent: 'space-between',
                        }}
                    >
                        <TextInput
                            name="order-longitude"
                            variant="contrast"
                            style={{
                                width: '100%',
                                height: '45px',
                            }}
                            placeholder="e.g. -122.47'"
                            value={longitude}
                            onChange={(e) => {
                                const lng = parseFloat(e.target.value);

                                if (!isNaN(lng)) {
                                    setLongitude(lng);

                                    updateRoi(latitude, lng);
                                }
                            }}
                        />
                    </Box>

                    <Divider style={{ margin: '20px 0px 20px 0px' }} />

                    <Typography variant="heading4" style={{ marginBottom: '4px' }}>
                        Start Date (UTC)
                    </Typography>
                    <Box
                        style={{
                            justifyContent: 'space-between',
                            position: 'relative',
                        }}
                    >
                        <DatePicker
                            variant="primary"
                            inverted
                            name="start-date"
                            defaultValue={searchStartDate}
                            style={{ width: '100%' }}
                            onChange={(newDate: NullableDate) => {
                                dispatch(setOpportunitySearchStartDate(newDate));
                                setSearchStartDate(newDate);
                            }}
                        />
                    </Box>

                    <Typography variant="heading4" style={{ marginBottom: '4px', marginTop: '20px' }}>
                        End Date (UTC)
                    </Typography>
                    <Box
                        style={{
                            justifyContent: 'space-between',
                        }}
                    >
                        <DatePicker
                            variant="primary"
                            inverted
                            name="end-date"
                            defaultValue={searchEndDate}
                            style={{ width: '100%' }}
                            onChange={(newDate: NullableDate) => {
                                dispatch(setOpportunitySearchEndDate(newDate));
                                setSearchEndDate(newDate);
                            }}
                        />
                    </Box>

                    <Divider style={{ margin: '40px 0px 20px 0px' }} />
                    <Box center="horizontal" style={{ width: '100%' }}>
                        <Text
                            onClick={() => {
                                resetPanel();
                            }}
                        >
                            Reset
                        </Text>
                    </Box>

                    <Box center="horizontal" grow style={{ alignItems: 'flex-end', marginBottom: '80px' }}>
                        <Typography variant="body2" strong color="red" style={{ textAlign: 'center' }}>
                            {submitError}
                        </Typography>
                    </Box>

                    <Box
                        h={80}
                        style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            backgroundColor: 'white',
                            width: '100%',
                            padding: '4px 8px',
                            boxShadow: '0px -1px 17px rgba(0, 0, 0, 0.19)',
                        }}
                        center="all"
                    >
                        <Tooltip position="top" label={validationError} style={{ width: '100%' }}>
                            <Button
                                variant={orderState === 'submitting' ? 'disabled' : 'action'}
                                style={{ width: '100%', height: '40px' }}
                                onClick={() => {
                                    setOrderState('submitting');
                                    setSubmitError('');

                                    const orderData: TaskingOrderRequest = {
                                        order_parameters: {
                                            name: orderName!,
                                            description: orderDescription,
                                        },
                                        datetime: `${searchStartDate?.toISOString()}/${searchEndDate?.toISOString()}`,
                                        geometry: {
                                            type: 'Point',
                                            coordinates: [longitude!, latitude!],
                                        },
                                    };

                                    OrdersAPI.createOrder(TASKING_PRODUCT, orderData)
                                        .then(() => {
                                            setOrderState('complete');
                                        })
                                        .catch((err) => {
                                            setOrderState('order');

                                            const { detail } = err.response.data;

                                            if (detail) {
                                                setSubmitError(detail);
                                            } else {
                                                setSubmitError('There was a problem creating this order.');
                                            }
                                        });
                                }}
                                disabled={validationError !== '' || orderState === 'submitting'}
                            >
                                <Box row center="all">
                                    {orderState === 'submitting' ? (
                                        <>
                                            <Spinner variant="Circle" size="Small" style={{ top: '-10px' }} />
                                        </>
                                    ) : (
                                        <>
                                            <OSKIcon code="shopping-cart" style={{ paddingRight: '8px' }} />
                                            <Text strong>Submit Request</Text>
                                        </>
                                    )}
                                </Box>
                            </Button>
                        </Tooltip>
                    </Box>
                </Box>
            </SidePanel>
        </Box>
    );
};

const mapStateToProps = (state: RootState) => {
    const { roi } = state.data.search;

    return {
        taskingAoi: roi,
    };
};

export default connect(mapStateToProps)(OpportunitySidePanel);
